<template>
    <div class="app-header"
         data-testid="app-header-component"
         :style="{top: top_offset + 'px'}">
        <terms-and-conditions data-testid="terms-and-conditions"
                              v-if="!auth.user.profile.created_by_reseller">
        </terms-and-conditions>

        <workflow-creator ref="workflow_creator"></workflow-creator>
        <div class="navbar navbar-toggleable-sm flex-row align-items-center justify-content-center bg-white">
            <!-- Open side - Naviation on mobile -->
            <a data-toggle="modal"
               data-target="#aside"
               data-testid="open-sidebar"
               class="d-lg-none mr-2">
                <i class="material-icons">&#xe5d2;</i>
            </a>
            <!-- / -->

            <a v-if="auth.user && auth.user.profile && !auth.user.profile.is_reseller"
               class="d-none d-lg-block mr-2 text-lg text-blackish"
               data-testid="toggle-sidebar"
               @click.prevent="toggleSidebar"
               :title="sidebar_folded ? 'Normal Layout' : 'Expanded Layout'">
                <i class="fa fa-bars"></i>
            </a>

            <!-- Page title - Bind to $state's title -->
            <div class="mb-0 h5 no-wrap"
                 id="page-title"
                 data-testid="page-title">
                <template v-if="isLargeEnough">
                    <template v-for="(route, idx) in validRoutes">
                        <template v-if="idx != 1">
                            <router-link :to="{name: route.name}">
                                {{ applyCustomPageTitle(route.name.capitalize()) }}
                            </router-link>
                            <span class="ml-1 mr-1 _100"
                                  v-if="idx < (validRoutes.length - 1) && currentRouteName.length > 0 && idx != 0">
                                >
                            </span>
                        </template>
                    </template>
                    <template v-if="pageTitle">
                        <span class="ml-1 mr-1 _100">
                            >
                        </span>
                        <component 
                            v-if="pageTitleComponent" 
                            :is="pageTitleComponent" 
                            v-bind="pageTitleComponentProps" 
                        />
                        <span>
                            {{ pageTitle }}
                        </span>
                    </template>
                    <template v-if="contact_name">
                        <span class="ml-1 mr-1 _100">
                            >
                        </span>
                        <span>
                            {{ contact_name }}
                        </span>
                    </template>
                    <template v-if="currentSelectedTeamName">
                        <span class="ml-1 mr-1 _100">
                            >
                        </span>
                        <el-tooltip effect="dark"
                                    placement="bottom-start"
                                    :content="currentSelectedTeamName">
                            <span>{{ currentSelectedTeamName | truncateText }}</span>
                        </el-tooltip>
                    </template>
                </template>
                <template v-else>
                    <router-link :to="{name: lastRoute.name}"
                                 v-if="lastRoute && lastRoute.name && !contact_name">
                        {{ applyCustomPageTitle(lastRoute.name.capitalize()) }}
                    </router-link>
                    <template v-else>
                        <span>
                            {{ contact_name }}
                        </span>
                    </template>
                </template>
            </div>

            <!-- navbar collapse -->
            <div class="collapse navbar-collapse"
                 data-testid="navabar-collapse"
                 id="collapse">
            </div>
            <!-- / navbar collapse -->

            <!-- navbar right -->
            <ul :class="{'nav': true, 'navbar-nav': true, 'ml-auto': true, 'flex-row': true, 'no-select': true}"
                data-testid="navbar-right">
                <li v-if="isLargeEnough && shouldShowPlatformLeadButton"
                  class="nav-item d-flex align-items-center mr-2"
                  data-testid="trial-redirect-button">
                        <span class="nav-link">
                            <el-tag class="font-weight-bold sales-connect-tag"
                                    type="primary"
                                    :disable-transitions="false"
                                    @close="dismissSalesButton"
                                    @click.native="redirectToContactSalesForm">
                                Connect with Sales
                            </el-tag>
                        </span>
                </li>
                <li v-if="isLargeEnough"
                    class="nav-item d-flex align-items-center mr-2"
                    data-testid="navabar-right-li">
                    <span class="nav-link">
                        <el-button class="default-app-button btn btn-xs"
                                   size="medium"
                                   :style="{border: !isAiEngineEnabled ? '2px solid #C4183C' : ''}"
                                   v-if="showAvaPromotionButton"
                                   plain
                                   @click="current_company?.transcription_settings?.call_transcription_enabled ? (showInfoBox = true) : null">
                            <sparkle-icon width="16" height="16" :color="isAiEngineEnabled ? '#9333EA' : '#C4183C'"/>
                            <span v-html="avaPromotionButtonLabel"></span>
                        </el-button>
                    </span>
                </li>
                <li v-if="isLargeEnough && show_missed_call_handler_button"
                    class="nav-item d-flex align-items-center mr-2"
                    data-testid="missed-call-handler-button">
                    <span class="nav-link">
                        <el-popover
                            placement="bottom-end"
                            width="400"
                            trigger="manual"
                            :visible-arrow="true"
                            popper-class="missed-call-handler-popover-popper"
                            v-model="show_missed_call_handler_popover">
                            <missed-call-handler-popover
                                v-if="show_missed_call_handler_popover"
                                ref="missedCallHandlerPopover"
                                :enabled="is_missed_call_handler_enabled"
                                @consent-required="handle_missed_call_handler_consent_required"
                                @status-changed="handle_missed_call_handler_status_changed"
                                @close="show_missed_call_handler_popover = false"
                            />
                            <el-button 
                                slot="reference"
                                class="default-app-button btn btn-xs"
                                size="medium"
                                :style="{border: !is_missed_call_handler_enabled ? '2px solid #C4183C' : ''}"
                                plain
                                @click="handle_missed_call_handler_button_click">
                                <sparkle-icon width="16" height="16" :color="is_missed_call_handler_enabled ? '#9333EA' : '#C4183C'"/>
                                <span v-html="missed_call_handler_button_label"></span>
                            </el-button>
                        </el-popover>
                    </span>
                </li>
                <li v-if="isLargeEnough"
                    class="nav-item d-flex align-items-center mr-2"
                    data-testid="navabar-right-li">
                    <span class="nav-link">
                        <talk-bridge v-if="auth.user.profile"
                                     data-testid="li-talk-bridge"/>
                    </span>
                </li>
                <li class="nav-item d-flex align-items-center mr-2"
                    v-if="usage && isLargeEnough && !isMobileSize && hasRole('Billing Admin') && !isTrial">
                    <span class="nav-link"
                          data-testid="navabar-right-li">
                        <router-link :to="{ name: 'Account', query: { tab: 'billing' }}">
                            <button class="btn btn-xs btn-outline _400"
                                    :style="{ color: '#ffffff', 'background-color': creditColor }">
                                <span>
                                    {{ usage.credits | fixRounding | toCurrency }}
                                </span>
                            </button>
                        </router-link>
                    </span>
                </li>

                <li class="nav-item d-flex align-items-center dropdown pos-stc-xs mr-2"
                    v-if="!whitelabel && !loading_whitelabel && !isMobileSize"
                    data-testid="navabar-right-li">
                    <a class="nav-link"
                       data-toggle="dropdown"
                       data-tour-step="2"
                       data-testid="question-circle-icon-a"
                       ref="helpMenu">
                        <i class="fa fa-question-circle text-2x text-danger"
                           style="vertical-align: -9px;"></i>
                    </a>
                    <!-- WAT:653
                         - for selfServeAccounts show Request a solution engineer and link to the chat support icon
                         - this also applies to account thas as internal sales rep or internal CSM
                         - for trial accounts show Request a Sales Rep call and link to their calendar
                         - for regular accounts show Request a CSM call and link to their calendar
                        -->
                    <HelpDropdown
                        :csm-info="csmInfo"
                        :debugging-info="debuggingInfo"
                        :is-self-served-account="auth.isSelfServedAccount()"
                        :role-name="roleName"
                        :sales-rep-info="salesRepInfo"
                        :service-code="serviceCode"
                        :show-csm-info="hasCsm && !csmInfo.internal && !isTrial"
                        :show-order-training-button="true"
                        :show-request-solution-engineer-button="(auth.isSelfServedAccount() || csmInfo.internal || salesRepInfo.internal)"
                        :show-sales-rep-info="hasSalesRep && !salesRepInfo.internal && isTrial"
                        :show-setup-guide="!isReseller"
                    >
                    </HelpDropdown>
                </li>

                <li class="nav-item dropdown"
                    :class="{'bg-dark': is_impersonated, 'bg-secondary-company': isOnSecondaryCompany && !is_impersonated}"
                    data-testid="navabar-right-li">
                    <a :class="{'nav-link': true, 'dropdown-toggle': true, 'text-ellipsis': true, 'text-white': is_impersonated || isOnSecondaryCompany}"
                       data-toggle="dropdown"
                       data-flip="false"
                       data-testid="user-logged-in"
                       data-tour-step="3"
                       ref="mainMenu">
                        <div class="profile-info d-flex align-items-center pl-1"
                             v-if="auth.user.profile">
                            <div class="d-flex flex-column ml-2"
                                 style="line-height: 1.2rem;">
                                <small class="dropdown-label"
                                       :class="{'text-white': is_impersonated || isOnSecondaryCompany}">
                                    {{ auth.user.profile.name }}
                                </small>
                                <span class="company-name _600"
                                      :class="{'text-white': is_impersonated || isOnSecondaryCompany}"
                                      v-if="current_company">
                                    {{ current_company.name }}
                                </span>
                            </div>
                            <i class="material-icons ml-2">keyboard_arrow_down</i>
                        </div>
                    </a>
                    <div class="dropdown-menu dropdown-menu-right dropdown-menu-overlay dropdown-menu-scale pull-right">
                        <div class="row"
                             v-if="auth.user.profile">
                            <div class="col-12">
                                <router-link :to="{ name: 'User Dialog', params: {user_id: auth.user.profile.id} }"
                                             class="dropdown-item">
                                    <i class="material-icons">person</i>
                                    Profile Settings
                                </router-link>
                            </div>
                        </div>

                        <div class="row nav-padding"
                             v-if="hasRole(['Company Agent', 'Company Admin']) && auth.user.profile && !auth.user.profile.is_reseller">
                            <div class="dropdown-item"
                                 data-testid="sleep-mode"
                                 @click.prevent>
                                <i class="material-icons fa-rotate-180">brightness_2</i>
                                <span>Sleep Mode</span>
                                <el-switch v-model="auth.user.profile.sleep_mode"
                                           class="ml-2 pull-right"
                                           active-color="#00a344"
                                           @change="changeSleepMode">
                                </el-switch>
                            </div>
                        </div>

                        <div class="dropdown-divider"></div>

                        <div class="row"
                             v-if="!loading_whitelabel && !whitelabel && !isResold">
                            <div class="col-12">
                                <a href="https://aloware.com/partnerships/"
                                   data-testid="my-referral-link-a"
                                   class="dropdown-item"
                                   target="_blank">
                                    <i class='fas fa-link'></i>
                                    My Referral Link
                                </a>
                            </div>
                        </div>

                        <div class="row"
                             v-if="!loading_whitelabel && !whitelabel">
                            <div class="col-12">
                                <a href="https://meetings.hubspot.com/alwr/aloware-demo"
                                   class="dropdown-item"
                                   data-testid="book-a-demo-a"
                                   target="_blank">
                                    <i class='material-icons'>perm_contact_calendar</i>
                                    Book a Demo
                                </a>
                            </div>
                        </div>

                        <div class="dropdown-divider"></div>

                        <div class="row"
                             v-if="!loading_whitelabel && !whitelabel">
                            <div class="col-12">
                                <a href="https://aloware.com/apps"
                                   class="dropdown-item"
                                   data-testid="desktop-apps-a"
                                   target="_blank">
                                    <i class="material-icons">desktop_mac</i>
                                    Desktop Apps
                                </a>
                            </div>
                        </div>

                        <div class="row"
                             v-if="!loading_whitelabel && !whitelabel">
                            <div class="col-12">
                                <a href="https://aloware.com/apps"
                                   class="dropdown-item"
                                   data-testid="mobile-apps-a"
                                   target="_blank">
                                    <i class="material-icons">devices</i>
                                    Mobile Apps
                                </a>
                            </div>
                        </div>

                        <div class="row"
                             v-if="!loading_whitelabel && !whitelabel">
                            <div class="col-12">
                                <a href="https://chrome.google.com/webstore/detail/aloware-click-to-call/kmchiimcbhaaadgpnelddelgiaeahobi?hl=en-US"
                                   class="dropdown-item"
                                   data-testid="chrome-ext-a"
                                   target="_blank">
                                    <i class="fab fa-chrome"></i>
                                    Chrome Ext. (CTI)
                                </a>
                            </div>
                        </div>

                        <div class="dropdown-divider"
                             v-if="auth.user.profile.has_multiple_access">
                        </div>

                        <template v-if="auth.user.profile.has_multiple_access">
                            <div class="row">
                                <div class="col-12">
                                    <router-link :to="{ name: 'Account Selector', query: { manual: true } }"
                                                 class="dropdown-item">
                                        <i class="fas fa-list"></i>
                                        Select Account
                                    </router-link>
                                </div>
                            </div>
                        </template>

                        <div class="dropdown-divider"></div>

                        <div class="row"
                             v-if="isOnSecondaryCompany && !is_impersonated"
                             @click="noClose($event)">
                            <div class="col-12">
                                <a class="dropdown-item"
                                   data-testid="back-to-main-a"
                                   @click="backToMainCompany()">
                                    <i class="material-icons">swap_horiz</i>
                                    Back to main company
                                </a>
                            </div>
                        </div>

                        <div class="row"
                             v-if="!isOnSecondaryCompany || is_impersonated"
                             data-testid="logout-row"
                             @click="noClose($event)">
                            <div class="col-12">
                                <a class="dropdown-item text-danger"
                                   data-testid="logout-a"
                                   @click="proceedToLogout()">
                                    <i class="material-icons">power_settings_new</i>
                                    {{ is_impersonated ? 'Stop impersonating' : 'Sign out' }}
                                </a>
                            </div>
                        </div>
                    </div>
                </li>
            </ul>
            <!-- / navbar right -->
        </div>
        <aloai-promotion-dialog :dialogVisible="showInfoBox"
                                @update:dialogVisible="showInfoBox = $event"/>
        <sales-information-dialog :dialogVisible="showSalesInfoBox"
                                  @update:dialogVisible="showSalesInfoBox = $event"/>

        <users-with-line-conflicts-modal
            :visible="showUsersWithLineConflictsModal"
            @close="showUsersWithLineConflictsModal = false"
        />

        <missed-call-handler-consent-dialog
            :visible="show_missed_call_consent_dialog"
            @update:visible="show_missed_call_consent_dialog = $event"
            @consent-accepted="handle_missed_call_handler_consent_accepted"
        />
    </div>
</template>

<script>
import axios from 'axios'
import VueCookies from 'vue-cookies'
import { mapActions, mapGetters, mapState } from 'vuex'
import { PRE_SIGNUP_SUBMISSION_TYPES } from '../../admin/constants/pre-signup'
import SparkleIcon from '../../images/app-icons/sparkle-bold-icon.vue'
import auth from '../auth'
import { isImpersonating, setCookie } from '../../utils/functions'
import RingGroupIcon from '../components/ring-groups/icons/ring-group-icon.vue'
import * as Roles from '../constants/roles'
import {
    acl_mixin,
    avatar_mixin,
    csm_info_mixin,
    helper_mixin,
    kyc_mixin,
    styling_mixin
} from '../mixins'
import AloaiPromotionDialog from './aloai/aloai-promotion-dialog.vue'
import MissedCallHandlerConsentDialog from './aloai/missed-call-handler-consent-dialog.vue'
import MissedCallHandlerPopover from './aloai/missed-call-handler-popover.vue'
import WorkflowCreator from "./automations/workflow-creator"
import HelpDropdown from './dropdowns/help-dropdown'
import SalesInformationDialog from './sales/sales-information-dialog.vue'
import UsersWithLineConflictsModal from './users/users-with-line-conflicts-modal.vue'
import TalkBridge from './talk-bridge'

export default {
    mixins: [
        styling_mixin,
        acl_mixin,
        avatar_mixin,
        kyc_mixin,
        helper_mixin,
        csm_info_mixin
    ],

    components: {
        WorkflowCreator,
        TalkBridge,
        HelpDropdown,
        SparkleIcon,
        AloaiPromotionDialog,
        SalesInformationDialog,
        RingGroupIcon,
        UsersWithLineConflictsModal,
        MissedCallHandlerConsentDialog,
        MissedCallHandlerPopover
    },

    data() {
        return {
            auth: auth,
            is_impersonated: isImpersonating(),
            env: null,
            branch: null,
            fake_number_purchase: null,
            fake_power_dialer_calls: null,
            fake_text_messages: null,
            fake_toll_free_verification: null,
            mock_a2p_brand_registration: null,
            mock_a2p_campaign_registration: null,
            pageTitle: null,
            pageTitleComponent: null,
            pageTitleComponentProps: {},
            is_live: false,
            whitelabel: false,
            loading_whitelabel: true,
            loading_sleep_mode: false,
            contact_name: null,
            window_size: null,
            showInfoBox: false,
            showSalesInfoBox: false,
            show_missed_call_consent_dialog: false,
            show_missed_call_handler_popover: false,
            Roles,
            salesButtonDismissed: false,
            showUsersWithLineConflictsModal: false,
        }
    },

    computed: {
        ...mapGetters('cache', ['isTrial']),
        ...mapGetters(['currentSelectedTeamName']),

        ...mapState({
            sidebar_folded: state => state.cache.sidebar_folded,
            current_company: state => state.cache.current_company,
            usage: state => state.usage,
            campaigns: state => state.campaigns,
        }),

        isOnSecondaryCompany() {
            return !!this.auth.user?.profile?.access_user_id
        },

        isLargeEnough() {
            let valid_sizes = ['xxl', 'xl', 'lg', 'md']
            if (valid_sizes.includes(this.$mq)) {
                return true
            }
            return false
        },

        isMobileSize() {
            return this.window_size <= 425
        },

        validRoutes() {
            return this.$route.matched.filter(route => route.name !== undefined && route.name !== 'Contact')
        },

        lastRoute() {
            if (this.validRoutes.length > 0) {
                return this.validRoutes.slice(-1)[0]
            }

            return null
        },

        creditColor() {
            if (this.usage) {
                if (this.usage.credits < 0) {
                    return '#f56c6c'
                } else if (this.usage.credits == 0) {
                    return '#090A0D'
                } else if (this.usage.credits <= 10) {
                    return '#fd7200'
                } else {
                    return '#2196f3'
                }
            }
        },

        currentRouteName() {
            return this.$route.name
        },

        isReseller() {
            return this.auth.user?.profile?.company?.reseller_id
        },

        salesRepInfo() {
            return this.auth.hasSalesRep() ?
                {
                    // we've a mix of combination for names so we use the name column as first option or the combination of first and last name
                    name: auth.user.profile.sales_rep.name || `${auth.user.profile.sales_rep.first_name || ''} ${auth.user.profile.sales_rep.last_name || ''}`,
                    email: auth.user.profile.sales_rep.email || '',
                    phone: auth.user.profile.sales_rep.phone_number || '',
                    calendar_link: auth.user.profile.sales_rep.calendar_link || '',
                    internal: Boolean(auth.user.profile.sales_rep.is_internal_account)
                }
                : {}
        },

        debuggingInfo() {
            return {
                env: this.env || 'production',
                branch: this.branch || '',
                fake_number_purchase: this.fake_number_purchase,
                fake_power_dialer_calls: this.fake_power_dialer_calls,
                fake_text_messages: this.fake_text_messages,
                fake_toll_free_verification: this.fake_toll_free_verification,
                mock_a2p_brand_registration: this.mock_a2p_brand_registration,
                mock_a2p_campaign_registration: this.mock_a2p_campaign_registration
            }
        },

        serviceCode() {
            return this.auth.user.profile ? `${this.auth.user.profile.company_id}-${this.auth.user.profile.id}` : ''
        },

        roleName() {
            return this.auth.user.profile.role_name
        },

        isAiEngineEnabled() {
            return this.current_company?.transcription_settings?.call_transcription_enabled && 
            this.current_company?.used_transcription_min < this.current_company?.plan?.included_transcription_min
        },

        avaPromotionButtonLabel() {
            return this.isAiEngineEnabled
            ? "AI Transcribe Ready"
            : `AI Transcribe <span style="color: #C4183C; font-weight: bold;">Off</span>`
        },

        showAvaPromotionButton() {
            return this.current_company?.transcription_enabled
        },

        shouldShowPlatformLeadButton() {
            const MAX_DISPLAY_DAYS = 30
            const cookieKey = `sales-button-dismissed-${this.auth.user.profile.id}`
            const buttonDismissed = this.salesButtonDismissed || this.$cookies.get(cookieKey)

            const shouldHideButton =
                !this.current_company ||
                this.current_company.is_trial ||
                this.current_company.subscription_active_days === null ||
                this.current_company.submission_type !== PRE_SIGNUP_SUBMISSION_TYPES.platform_led.id ||
                buttonDismissed

            if (shouldHideButton) {
                return false
            }

            return this.current_company.subscription_active_days <= MAX_DISPLAY_DAYS
        },

        show_missed_call_handler_button() {
            return this.current_company?.aloai_missed_call_handler_feature_enabled === true
        },

        is_missed_call_handler_enabled() {
            return this.current_company?.aloai_missed_call_handler_enabled === true
        },

        can_manage_missed_call_handler() {
            return !!this.auth?.user?.profile && this.hasRole([Roles.COMPANY_ADMIN, Roles.BILLING_ADMIN])
        },

        missed_call_handler_button_label() {
            return this.is_missed_call_handler_enabled
                ? "AI Call Rescue Ready"
                : `AI Call Rescue <span style="color: #C4183C; font-weight: bold;">Off</span>`
        },

    },

    created() {
        this.$cookies = VueCookies
        this.window_size = window.screen.width
        window.addEventListener("resize", this.windowResize)
        

        this.getWhitelabelStatus()

        if (!this.isLargeEnough) {
            this.setSidebarFolded(false)
        }

        VueEvent.listen('page_title_updated', (page_title_config) => {
            if (typeof page_title_config === 'string') {
                this.pageTitle = page_title_config
                this.pageTitleComponent = null
                this.pageTitleComponentProps = {}
                return
            }

            const { title, component, props } = page_title_config
            this.pageTitle = title
            this.pageTitleComponent = component
            this.pageTitleComponentProps = props
        })

        VueEvent.listen('user_logout', () => {
            if (auth.user.authenticated) {
                auth.logout().catch(() => {
                    auth.clear()
                }).finally(() => {
                    this.logoutUser()
                })
            }
        })

        VueEvent.listen('show_users_with_line_conflicts_modal', (showModal) => {
            this.showUsersWithLineConflictsModal = showModal
        })

        setInterval(() => {
            this.is_live = (window.Echo && window.Echo.connector.pusher.connection.connection !== null)
        }, 1000)
    },

    methods: {
        windowResize() {
            this.window_size = window.screen.width
        },

        redirectToContactSalesForm() {
            this.showSalesInfoBox = true
        },

        applyCustomPageTitle(title) {
            switch (title.toLowerCase()) {
                case 'account management portal':
                    return 'Dashboard'
                case 'ring groups':
                    return 'Inboxes'
                default:
                    return title
            }
        },

        getWhitelabelStatus() {
            this.loading_whitelabel = true
            axios.get('/get-statics').then(res => {
                this.whitelabel = res.data.whitelabel
                this.loading_whitelabel = false
            }).catch(err => {
                console.log(err)
                this.loading_whitelabel = false
                this.$root.handleErrors(err.response)
            })
        },

        changeSleepMode() {
            this.loading_sleep_mode = true
            axios.post('/api/v1/profile', {
                sleep_mode: this.auth.user.profile.sleep_mode
            }).then(res => {
                const message = 'Sleep mode ' + ((this.auth.user.profile.sleep_mode) ? 'activated' : 'deactivated')
                this.loading_sleep_mode = false
                this.$notify({
                    offset: 95,
                    title: 'Sleep Mode',
                    message: message + '. Reloading ...',
                    type: 'success'
                })
                localStorage.setItem('PREVIOUS_ROUTE_NAME', 'null')
                VueEvent.fire('reloadPageComponent')
            }).catch(err => {
                console.log(err)
                this.$root.handleErrors(err.response)
                this.loading_sleep_mode = false
            })
        },

        noClose(event) {
            if (event) {
                event.stopPropagation()
            }
        },


        async handle_missed_call_handler_button_click(event) {
            event?.stopPropagation()

            const canManage = this.can_manage_missed_call_handler

            try {
                const response = await axios.get(`/api/v1/consent/aloai_missed_call_handler`)
                const consentData = response.data
                const hasAcceptedConsent = consentData.has_consent && consentData.consent_decision === 'accepted'
                
                if (canManage && (consentData.show_modal || (consentData.has_consent && !hasAcceptedConsent))) {
                    this.show_missed_call_consent_dialog = true
                } else {
                    this.show_missed_call_handler_popover = true
                }
            } catch (error) {
                console.error('Failed to check consent:', error)
                if (canManage) {
                    this.show_missed_call_consent_dialog = true
                } else {
                    this.show_missed_call_handler_popover = true
                }
            }
        },

        async handle_missed_call_handler_consent_required(shouldActivate) {
            try {
                const response = await axios.get(`/api/v1/consent/aloai_missed_call_handler`)
                const consentData = response.data
                const hasAcceptedConsent = consentData.has_consent && consentData.consent_decision === 'accepted'
                
                if (consentData.show_modal || (consentData.has_consent && !hasAcceptedConsent)) {
                    this.show_missed_call_consent_dialog = true
                } else if (hasAcceptedConsent && shouldActivate) {
                    this.$nextTick(() => {
                        if (this.$refs.missedCallHandlerPopover) {
                            this.$refs.missedCallHandlerPopover.handle_missed_call_handler_toggle(true)
                        }
                    })
                }
            } catch (error) {
                console.error('Failed to check consent:', error)
                this.show_missed_call_consent_dialog = true
            }
        },

        async handle_missed_call_handler_consent_accepted() {
            if (this.current_company) {
                const response = await axios.get(`/api/v1/company/${this.current_company.id}`)
                this.setCurrentCompany(response.data)
            }
        },

        async handle_missed_call_handler_status_changed(enabled) {
            if (this.current_company) {
                const response = await axios.get(`/api/v1/company/${this.current_company.id}`)
                this.setCurrentCompany(response.data)
            }
        },

        handle_missed_call_handler_click_outside_popover(event) {
            const popoverElement = document.querySelector('.missed-call-handler-popover-popper')
            const buttonElement = event.target.closest('[data-testid="missed-call-handler-button"]')
            const popoverContent = event.target.closest('.missed-call-handler-popover')
            
            if (popoverElement && 
                !popoverElement.contains(event.target) && 
                !buttonElement && 
                !popoverContent) {
                this.show_missed_call_handler_popover = false
            }
        },

        toggleSidebar() {
            this.setSidebarFolded(!this.sidebar_folded)
            VueEvent.fire('content_resized')
        },

        proceedToLogout() {
            this.logout()
        },

        backToMainCompany() {
            const mainUserId = this.auth.user.profile.access_user_id
            if (!mainUserId) {
                return
            }

            this.auth.switchCompany(mainUserId).then((res) => {
                setCookie('selected_company_id', res.data.company_id, 30)
                window.location.href = '/'
            }).catch((err) => {
                console.error(err)
                this.$root.handleErrors(err.response)
            })
        },

        dismissSalesButton() {
            // Set a cookie to remember that the user dismissed the button
            this.$cookies.set(`sales-button-dismissed-${this.auth.user.profile.id}`, true, {expires: 30})

            this.salesButtonDismissed = true
        },

        ...mapActions('cache', ['setSidebarFolded', 'resetCache', 'setCurrentCompany']),
        ...mapActions(['logoutUser'])
    },


    beforeCreate() {
        axios.get('/build-info.json').then(res => {
            this.env = res.data.env
            this.branch = res.data.branch
            this.fake_number_purchase = res.data.fake_number_purchase
            this.fake_power_dialer_calls = res.data.fake_power_dialer_calls
            this.fake_text_messages = res.data.fake_text_messages
            this.fake_toll_free_verification = res.data.fake_toll_free_verification
            this.mock_a2p_brand_registration = res.data.mock_a2p_brand_registration
            this.mock_a2p_campaign_registration = res.data.mock_a2p_campaign_registration
        }).catch(err => {
            console.log(err)
        })
    },

    destroyed() {
        window.removeEventListener("resize", this.windowResize)
        document.removeEventListener('click', this.handle_missed_call_handler_click_outside_popover)
    },

    watch: {
        show_missed_call_handler_popover(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    document.addEventListener('click', this.handle_missed_call_handler_click_outside_popover)
                })
            } else {
                document.removeEventListener('click', this.handle_missed_call_handler_click_outside_popover)
            }
        },
        '$route.path': function () {
            this.pageTitle = null
            this.contact_name = null
        },

        is_live: {
            handler(is_live) {
                this.$emit('live:status', is_live)
            }
        },
    },
}
</script>

<style scoped>
.el-button {
    font-weight: 700 !important;
}

.sales-connect-tag {
    cursor: pointer;
    padding: 10px;
    font-size: 16px;
    height: auto;
    line-height: 1;
    border-radius: 4px;
}

.bg-secondary-company {
    background-color: #1a3a5c;
    border-radius: 4px;
}
</style>

<style>
.missed-call-handler-popover-popper {
    padding: 0 !important;
}
</style>
